// src/hooks/useScheduledTasks.ts

//-tech-debt-react-query;
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchItems, postItem, putItem, deleteItem, patchItem } from '../support/queries';
import { ScheduledTask, ScheduledTaskExecution } from '../types/taskManager';

interface UseScheduledTasksOptions {
  status?: string;
  enabled?: boolean;
  job_type?: string;
  tag?: string;
}

// Make fields optional to match Partial<ScheduledTask>
interface CreateScheduledTaskParams {
  name: string;
  description?: string;
  job_type: string;
  job_payload: Record<string, any>;
  interval_type: 'once' | 'minutely' | 'hourly' | 'daily' | 'weekly' | 'monthly' | 'cron';
  interval_value?: number;
  cron_expression?: string;
  start_time?: string;
  end_time?: string;
  timezone_name?: string;
  enabled?: boolean;
  max_failures?: number;
  created_by?: string;
  tags?: string[];
}

export const useScheduledTasks = (filters?: UseScheduledTasksOptions) => {
  const queryClient = useQueryClient();

  // Build query params
  const params = new URLSearchParams();
  if (filters?.status) params.append('status', filters.status);
  if (filters?.enabled !== undefined) params.append('enabled', String(filters.enabled));
  if (filters?.job_type) params.append('job_type', filters.job_type);
  if (filters?.tag) params.append('tag', filters.tag);

  const endpoint = `queue/scheduled-tasks/${params.toString() ? '?' + params.toString() : ''}`;
  const hasActiveFilter = filters?.status === 'active' || !filters?.status;
  // Fetch tasks
  const query = useQuery<ScheduledTask[]>({
    queryKey: ['scheduled-tasks', filters],
    queryFn: () => fetchItems(endpoint),
    ...{
        refetchInterval: hasActiveFilter ? 30000 : 60000, // 30s for active, 60s for others
        staleTime: hasActiveFilter ? 20000 : 45000,
        refetchOnWindowFocus: hasActiveFilter, // Only refetch on focus if viewing active tasks
      },
  });

  // Create task - accept Partial<ScheduledTask> to match component usage
  const createMutation = useMutation({
    mutationFn: (data: Partial<ScheduledTask>) => {
      // Validate required fields
      if (!data.name || !data.job_type || !data.job_payload || !data.interval_type) {
        throw new Error('Missing required fields: name, job_type, job_payload, interval_type');
      }
      return postItem('queue/scheduled-tasks/', data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scheduled-tasks'] });
      queryClient.invalidateQueries({ queryKey: ['task-statistics'] });
    },
    onError: (error: any) => {
      console.error('Failed to create task:', error);
    },
  });

  // Update task (full update)
  const updateMutation = useMutation({
    mutationFn: (data: Partial<ScheduledTask> & { id: number }) =>
      putItem(`queue/scheduled-tasks/${data.id}/`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scheduled-tasks'] });
      queryClient.invalidateQueries({ queryKey: ['scheduled-task-details'] });
    },
  });

  // Partial update task
  const patchMutation = useMutation({
    mutationFn: (data: Partial<ScheduledTask> & { id: number }) =>
      patchItem(`queue/scheduled-tasks/${data.id}/`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scheduled-tasks'] });
      queryClient.invalidateQueries({ queryKey: ['scheduled-task-details'] });
    },
  });

  // Delete task
  const deleteMutation = useMutation({
    mutationFn: (id: number) => deleteItem(`queue/scheduled-tasks/${id}/`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scheduled-tasks'] });
      queryClient.invalidateQueries({ queryKey: ['task-statistics'] });
    },
  });

  // Run now
  const runNowMutation = useMutation({
    mutationFn: (id: number) => postItem(`queue/scheduled-tasks/${id}/run_now/`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scheduled-tasks'] });
      queryClient.invalidateQueries({ queryKey: ['job-executions'] });
      queryClient.invalidateQueries({ queryKey: ['scheduled-executions'] });
      queryClient.invalidateQueries({ queryKey: ['task-statistics'] });
    },
  });

  // Enable/Disable
  const enableMutation = useMutation({
    mutationFn: (id: number) => postItem(`queue/scheduled-tasks/${id}/enable/`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scheduled-tasks'] });
      queryClient.invalidateQueries({ queryKey: ['task-statistics'] });
    },
  });

  const disableMutation = useMutation({
    mutationFn: (id: number) => postItem(`queue/scheduled-tasks/${id}/disable/`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scheduled-tasks'] });
      queryClient.invalidateQueries({ queryKey: ['task-statistics'] });
    },
  });

  // Pause/Resume
  const pauseMutation = useMutation({
    mutationFn: (id: number) => postItem(`queue/scheduled-tasks/${id}/pause/`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scheduled-tasks'] });
      queryClient.invalidateQueries({ queryKey: ['task-statistics'] });
    },
  });

  const resumeMutation = useMutation({
    mutationFn: (id: number) => postItem(`queue/scheduled-tasks/${id}/resume/`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scheduled-tasks'] });
      queryClient.invalidateQueries({ queryKey: ['task-statistics'] });
    },
  });

  return {
    // Data
    tasks: query.data || [],
    loading: query.isLoading,
    error: query.error,

    // Actions
    refetch: query.refetch,
    createTask: createMutation.mutateAsync,
    updateTask: updateMutation.mutateAsync,
    patchTask: patchMutation.mutateAsync,
    deleteTask: deleteMutation.mutateAsync,
    runNow: runNowMutation.mutateAsync,
    enable: enableMutation.mutateAsync,
    disable: disableMutation.mutateAsync,
    pause: pauseMutation.mutateAsync,
    resume: resumeMutation.mutateAsync,

    // Loading states
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isPatching: patchMutation.isPending,
    isDeleting: deleteMutation.isPending,
    isRunning: runNowMutation.isPending,

    // Errors
    createError: createMutation.error,
    updateError: updateMutation.error,
    deleteError: deleteMutation.error,
  };
};

// Scheduled Task Details Hook
export const useScheduledTaskDetails = (taskId: number | null) => {
  const query = useQuery<ScheduledTask>({
    queryKey: ['scheduled-task-details', taskId],
    queryFn: () => fetchItems(`queue/scheduled-tasks/${taskId}/`),
    ...{
        enabled: !!taskId,
        refetchInterval: 30000,
      },
  });

  return {
    task: query.data,
    loading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
};

// Tasks Due Now Hook
export const useDueScheduledTasks = () => {
  const query = useQuery<ScheduledTask[]>({
    queryKey: ['due-scheduled-tasks'],
    queryFn: () => fetchItems('queue/scheduled-tasks/due_now/'),
    ...{
        refetchInterval: 15000,
      },
  });

  return {
    dueTasks: query.data || [],
    loading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
};

// Task Statistics Hook
export const useTaskStatistics = () => {
  const query = useQuery({
    queryKey: ['task-statistics'],
    queryFn: () => fetchItems('queue/scheduled-tasks/statistics/'),
    ...{
        refetchInterval: 30000,
      },
  });

  return {
    statistics: query.data,
    loading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
};

// Task Executions Hook (for specific task)
export const useTaskExecutions = (taskId: number | null) => {
  const query = useQuery<ScheduledTaskExecution[]>({
    queryKey: ['task-executions', taskId],
    queryFn: () => fetchItems(`queue/scheduled-tasks/${taskId}/executions/`),
    ...{
        enabled: !!taskId,
        refetchInterval: 15000,
      },
  });

  return {
    executions: query.data || [],
    loading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
};